import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { Col, Row } from "reactstrap";
import {
  getFormattedContactNumber,
  getFormattedOptions,
  Toast,
  uploadImage,
} from "utility/helpers";
import {
  fetchAllAgencies,
  fetchAllAgencyProjects,
} from "services/services.projects";
import { AddAccount } from "services/services.accounts";
import ImgContainer from "components/common/img";
import defaultUserImage from "assets/img/default_user.png";
import { FILE_TYPE_IMAGE } from "constants/fileType";
import { ALL_OPTION } from "utility/constants";
import styled from "styled-components";
import {
  HeaderText,
  ImageWrapper,
  UploadButton,
  UploadInput,
} from "assets/js/Styles";
import AgencyForm from "./AgencyForm";
import { getAgencySelectedPrograms, validateAgencyForm } from "./utility";
import { POSITION_AGENCY } from "../constants";

const Wrapper = styled.div`
  margin-top: 50px;
`;

const FormSection = styled.div`
  margin-top: 44px;
  padding-bottom: 4rem;
`;

const HeaderActions = styled.div`
  margin-top: 24px;
  display: grid;
  grid-auto-flow: column;
  justify-content: space-between;
  align-items: center;
`;

const AgencyDetails = () => {
  const location = useLocation();
  const agency = location.state?.agency || null;

  const [isLoading, setIsLoading] = useState(false);
  const [agencies, setAgencies] = useState([]);
  const [selectedAgency, setSelectedAgency] = useState(null);
  const [programOptions, setProgramOptions] = useState([]);

  const [formFields, setFormFields] = useState({
    userId: "",
    lastname: "",
    firstname: "",
    middleInitial: "",
    email: "",
    mobile: "",
    agency: "",
    cos: "",
    cosNumber: "",
    password: "",
    passwordConfirmation: "",
    photo: "",
    photoName: "",
    cosFirstName: "",
    cosLastName: "",
    programs: [],
  });

  const navigate = useNavigate();

  const setAgencyDetails = () => {
    if (agency.photo) {
      const imgContainer = document.querySelector(".agency-img");
      imgContainer.src = agency.photo;
    }

    const option = agencies.find((__agency) => __agency.id === agency.agencyId);

    if (option) setSelectedAgency(option);

    setFormFields({
      ...formFields,
      userId: agency.userId,
      lastname: agency.lastName,
      firstname: agency.firstName,
      middleInitial: agency.middleName,
      mobile: getFormattedContactNumber(agency.phoneNumber) || "",
      email: agency.email,
      cos: agency.cosName || "",
      cosNumber: getFormattedContactNumber(agency.cosContact) || "",
      photo: agency.photo,
      cosFirstName: agency.cosFirstName,
      cosLastName: agency.cosLastName || agency.cosName,
      agency: option,
    });

    setIsLoading(false);
  };

  const sendData = (formData) => {
    AddAccount({
      url: !agency ? "/auth/signup" : "/auth/update-user",
      method: agency ? "PUT" : "POST",
      formData,
      callback: ({ isSuccess, msg }) => {
        if (isSuccess) {
          setIsLoading(false);

          navigate("/directory", {
            state: { view: POSITION_AGENCY },
          });

          Toast({
            type: 1,
            content: msg,
          });
        } else {
          setIsLoading(false);

          Toast({
            type: 4,
            content: msg,
          });
        }
      },
    });
  };

  const handleOnSubmit = async () => {
    const isValid = await validateAgencyForm(formFields);

    if (typeof isValid === "boolean") {
      setIsLoading(true);

      const formData = {
        username: formFields.email,
        middleName: formFields.middleInitial,
        firstName: formFields.firstname,
        lastName: formFields.lastname,
        phoneNumber: formFields.mobile?.slice(1),
        agency: formFields.agency?.value,
        accountType: 2, // hard-coded value "Agency"
        cosFirstName: formFields.cosFirstName,
        cosLastName: formFields.cosLastName,
        cosContact: formFields.cosNumber?.slice(1),
        programIds: getAgencySelectedPrograms(formFields, programOptions),
      };

      if (agency) formData.userId = formFields.userId;
      if (!agency) formData.password = formFields.password;

      if (typeof formFields.photo !== "string") {
        const fileName = await uploadImage({
          photo: formFields.photo,
          photoName: formFields.photoName,
        });
        formData.photo = fileName;
      }

      sendData(formData);
    } else {
      Toast({
        type: 4,
        content: isValid,
      });
    }
  };

  const fetchData = () => {
    fetchAllAgencies({
      callback: (data) => {
        const newData = getFormattedOptions("name", data);

        setAgencies(newData);
      },
    });
    setIsLoading(false);
  };

  const handleFileUpload = (evt) => {
    const file = evt.target.files[0];
    const reader = new FileReader();
    const fileTye = file.type.split("/")[0];

    if (fileTye === FILE_TYPE_IMAGE) {
      reader.onloadend = () => {
        const imgContainer = document.querySelector(".agency-img");

        const fileName = agency
          ? `${
              agency.userId
            }-${formFields.lastname.toLocaleLowerCase()}-${Date.now()}`
          : `${formFields.lastname.toLocaleLowerCase()}-${Date.now()}`;

        setFormFields({
          ...formFields,
          photo: file,
          photoName: fileName,
        });

        imgContainer.src = reader.result;
      };

      reader.readAsDataURL(file);
    } else {
      Toast({
        type: 4,
        content: "File uploaded must be an image.",
      });
    }
  };

  const fetchPrograms = () => {
    fetchAllAgencyProjects({
      url: `/agency/${formFields.agency?.value}`,
      callback: (data) => {
        let options = data?.Projects;

        if (options.length !== 0) {
          options = getFormattedOptions("name", options);

          options.unshift({
            label: "All Programs",
            value: ALL_OPTION,
          });
        }

        setProgramOptions(options);
      },
    });
  };

  const setDefaultSelectedPrograms = () => {
    setFormFields({
      ...formFields,
      programs: [
        {
          label: "All Programs",
          value: ALL_OPTION,
        },
      ],
    });
  };

  useEffect(() => {
    if (
      formFields.programs.length > 1 &&
      formFields.programs?.some((project) => project.value === ALL_OPTION)
    ) {
      setDefaultSelectedPrograms();
    }

    if (
      programOptions.length !== 0 &&
      formFields.programs.length !== 0 &&
      formFields.programs?.some((project) => project.value !== ALL_OPTION) &&
      programOptions.length - 1 === formFields.programs.length
    ) {
      setDefaultSelectedPrograms();
    }
  }, [formFields.programs]);

  useEffect(() => {
    if (agency && programOptions.length !== 0) {
      const programs = agency?.programIds;

      const options = programOptions.filter((op) => programs.includes(op.id));

      setFormFields({
        ...formFields,
        programs: options,
      });
    }
  }, [programOptions, agency]);

  useEffect(() => {
    setIsLoading(true);
    //  fetch all agency
    fetchData();
  }, []);

  useEffect(() => {
    if (agency !== null && agencies.length) {
      setAgencyDetails();
    }
  }, [agency, agencies]);

  useEffect(() => {
    if (formFields.agency) {
      fetchPrograms();
    }
  }, [formFields.agency]);

  return (
    <Wrapper>
      <Col className="container">
        <HeaderActions>
          <HeaderText>{!agency ? "Add Agency" : "Agency Details"}</HeaderText>
        </HeaderActions>

        <FormSection>
          <Row>
            <Col
              xs="12"
              sm="6"
              lg="4"
            >
              <ImageWrapper>
                <ImgContainer
                  alt="agency-img"
                  className="agency-img"
                  src={defaultUserImage}
                  width="196px"
                  height="196px"
                />

                <UploadButton htmlFor="file-upload">
                  {"Upload Photo"}
                  <UploadInput
                    id="file-upload"
                    type="file"
                    onChange={handleFileUpload}
                  />
                </UploadButton>
              </ImageWrapper>
            </Col>
            <Col
              xs="12"
              sm="6"
              lg="8"
            >
              <AgencyForm
                handleOnSubmit={handleOnSubmit}
                formFields={formFields}
                setFormFields={setFormFields}
                agency={agency}
                agencies={agencies}
                selectedAgency={selectedAgency}
                setSelectedAgency={setSelectedAgency}
                isLoading={isLoading}
                programOptions={programOptions}
              />
            </Col>
          </Row>
        </FormSection>
      </Col>
    </Wrapper>
  );
};

export default AgencyDetails;
